import { merge } from 'lodash';
import { Checkbox, Slider, TextField } from '@mui/material';
import { DatePicker, DateTimePicker, TimePicker } from '@mui/x-date-pickers';
import { BasicConfig, type Config } from '@react-awesome-query-builder/mui';

// eslint-disable-next-line import/no-cycle
import {
	MaterialButton,
	MaterialConjs,
	MaterialCurrencyField,
	MaterialFieldSelect,
	MaterialModuleSearch,
	MaterialOperatorSelect,
	MaterialProvider,
	MaterialSearchSelect
} from './query-builder-components';

const parseComplexValue = (val, fieldDef, wgtDef, isForDisplay) => (isForDisplay ? `"${val}"` : JSON.stringify(val));

const numericalOperators = [
	'equal',
	'not_equal',
	'less',
	'less_or_equal',
	'greater',
	'greater_or_equal',
	'is_null',
	'is_not_null'
];

// Remove proximity operator
const { proximity, ...ops } = BasicConfig.operators;

// Build config
const QueryBuilderConfig = ({ t, mainTheme }) => {
	const config: Config = {
		settings: {
			renderField: props => <MaterialFieldSelect {...props} />,
			renderOperator: props => <MaterialOperatorSelect {...props} />,
			renderConjs: props => <MaterialConjs {...props} />,
			renderButton: props => <MaterialButton {...props} />,
			renderProvider: props => <MaterialProvider {...props} mainTheme={mainTheme} />,
			addGroupLabel: t('QB_ADD_GROUP'),
			addRuleLabel: t('QB_ADD_RULE'),
			delGroupLabel: t('QB_DEL_GROUP'),
			setOpOnChangeField: ['default', 'first'],
			removeIncompleteRulesOnLoad: false,
			removeEmptyGroupsOnLoad: false,
			showErrorMessage: true,
			showNot: false
		},
		conjunctions: BasicConfig.conjunctions,
		ctx: BasicConfig.ctx,
		operators: {
			equal: {
				label: t('OPERATOR_EQUALS'),
				jsonLogic: 'equals'
			},
			not_equal: {
				label: t('OPERATOR_NOT_EQUAL'),
				jsonLogic: 'not_equal'
			},
			less: {
				label: t('OPERATOR_SMALLER'),
				jsonLogic: 'smaller'
			},
			less_or_equal: {
				label: t('OPERATOR_SMALLER_EQUAL'),
				jsonLogic: 'smaller_equal'
			},
			greater: {
				label: t('OPERATOR_GREATER'),
				jsonLogic: 'greater'
			},
			greater_or_equal: {
				label: t('OPERATOR_GREATER_EQUAL'),
				jsonLogic: 'greater_equal'
			},
			like: {
				label: t('OPERATOR_LIKE'),
				jsonLogic: 'like'
			},
			not_like: {
				label: t('OPERATOR_NOT_LIKE'),
				jsonLogic: 'not_like'
			},
			// starts_with: {
			// 	label: t('OPERATOR_STARTS_WITH'),
			// 	jsonLogic: 'starts_with'
			// },
			// ends_with: {
			// 	label: t('OPERATOR_ENDS_WITH'),
			// 	jsonLogic: 'ends_with'
			// },
			// between: {
			// 	label: t('OPERATOR_RANGE'),
			// 	textSeparators: [null, t('QB_TEXT_SEPARATOR_AND')],
			// 	jsonLogic: 'between'
			// },
			// not_between: {
			// 	label: t('OPERATOR_NOT_BETWEEN'),
			// 	textSeparators: [null, t('QB_TEXT_SEPARATOR_AND')],
			// 	jsonLogic: 'not_between'
			// },
			is_null: {
				label: t('OPERATOR_IS_NULL'),
				jsonLogic: 'is_null'
			},
			is_not_null: {
				label: t('OPERATOR_IS_NOT_NULL'),
				jsonLogic: 'is_not_null'
			}
		},
		fields: {},
		widgets: {
			text: {
				type: 'text',
				jsType: 'string',
				valueSrc: 'value',
				factory: props => (
					<TextField
						className="w-full"
						name={props.field.toString()}
						variant="outlined"
						size="small"
						defaultValue={props.value}
						onBlur={e => props.setValue(e.target.value)}
						disabled={props.readonly}
					/>
				),
				formatValue: parseComplexValue
			},
			boolean: {
				type: 'boolean',
				jsType: 'number',
				valueSrc: 'value',
				factory: props => (
					<Checkbox
						name={props.field.toString()}
						size="small"
						checked={props.value === 1}
						onChange={e => props.setValue(e.target.checked ? 1 : 0)}
						disabled={props.readonly}
					/>
				),
				formatValue: (val, fieldDef, wgtDef, isForDisplay) => (isForDisplay ? val : JSON.stringify(!!val))
			},
			number: {
				type: 'number',
				jsType: 'number',
				valueSrc: 'value',
				factory: props => (
					<TextField
						className="w-full"
						name={props.field.toString()}
						type="number"
						variant="outlined"
						size="small"
						defaultValue={props.value}
						onBlur={e => props.setValue(e.target.value)}
						disabled={props.readonly}
					/>
				)
			},
			decimal: {
				type: 'decimal',
				jsType: 'number',
				valueSrc: 'value',
				factory: props => (
					<TextField
						className="w-full"
						name={props.field.toString()}
						type="number"
						variant="outlined"
						size="small"
						defaultValue={props.value}
						onBlur={e => props.setValue(e.target.value)}
						disabled={props.readonly}
					/>
				)
			},
			currency: {
				type: 'currency',
				jsType: 'number',
				valueSrc: 'value',
				factory: props => (
					<MaterialCurrencyField
						name={props.field.toString()}
						value={props.value}
						sendChange={props.setValue}
						disabled={!!props.readonly}
					/>
				)
			},
			date: {
				type: 'date',
				jsType: 'object',
				valueSrc: 'value',
				factory: props => (
					<DatePicker
						className="w-full"
						format="DD/MM/YYYY"
						formatDensity="dense"
						slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
						value={props.value}
						onChange={newValue => props.setValue(newValue)}
						disabled={props.readonly}
					/>
				),
				formatValue: parseComplexValue
			},
			time: {
				type: 'time',
				jsType: 'object',
				valueSrc: 'value',
				factory: props => (
					<TimePicker
						className="w-full"
						format="HH:mm"
						formatDensity="dense"
						slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
						value={props.value}
						onChange={newValue => props.setValue(newValue)}
						disabled={props.readonly}
						desktopModeMediaQuery=""
					/>
				),
				formatValue: parseComplexValue
			},
			datetime: {
				type: 'datetime',
				jsType: 'object',
				valueSrc: 'value',
				factory: props => (
					<DateTimePicker
						className="w-full"
						format="DD/MM/YYYY HH:mm"
						formatDensity="dense"
						slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
						value={props.value}
						onChange={newValue => props.setValue(newValue)}
						disabled={props.readonly}
						desktopModeMediaQuery=""
					/>
				),
				formatValue: parseComplexValue
			},
			list: {
				type: 'list',
				jsType: 'number',
				valueSrc: 'value',
				factory: props => (
					<MaterialSearchSelect
						name={props.field.toString()}
						systable={`${props.fieldDefinition.tableName}`}
						value={props.value}
						sendChange={props.setValue}
						disabled={!!props.readonly}
					/>
				),
				formatValue: parseComplexValue
			},
			module: {
				type: 'module',
				jsType: 'string',
				valueSrc: 'value',
				factory: props => (
					<MaterialModuleSearch
						name={props.field.toString()}
						module={`${props.fieldDefinition.tableName}`}
						uid={props.value}
						sendChange={props.setValue}
						disabled={!!props.readonly}
					/>
				),
				formatValue: parseComplexValue
			},
			slider: {
				type: 'slider',
				jsType: 'number',
				valueSrc: 'value',
				factory: props => (
					<Slider
						name={props.field.toString()}
						defaultValue={props.value}
						onChange={(_event, value) => props.setValue(value)}
						disabled={props.readonly}
					/>
				)
			}
		},
		types: {
			text: {
				defaultOperator: 'equal',
				mainWidget: 'text',
				widgets: { text: { operators: ['equal', 'not_equal', 'like', 'not_like', 'is_null', 'is_not_null'] } }
			},
			number: {
				defaultOperator: 'equal',
				mainWidget: 'number',
				widgets: { number: { operators: numericalOperators } }
			},
			decimal: {
				defaultOperator: 'equal',
				mainWidget: 'decimal',
				widgets: { decimal: { operators: numericalOperators } }
			},
			date: {
				defaultOperator: 'equal',
				mainWidget: 'date',
				widgets: { date: { operators: numericalOperators } }
			},
			time: {
				defaultOperator: 'equal',
				mainWidget: 'time',
				widgets: { time: { operators: numericalOperators } }
			},
			datetime: {
				defaultOperator: 'equal',
				mainWidget: 'datetime',
				widgets: { datetime: { operators: numericalOperators } }
			},
			currency: {
				defaultOperator: 'equal',
				mainWidget: 'currency',
				widgets: { currency: { operators: ['equal', 'not_equal', 'is_null', 'is_not_null'] } }
			},
			list: {
				defaultOperator: 'equal',
				mainWidget: 'list',
				widgets: { list: { operators: ['equal', 'not_equal', 'is_null', 'is_not_null'] } }
			},
			module: {
				defaultOperator: 'equal',
				mainWidget: 'module',
				widgets: { module: { operators: ['equal', 'not_equal', 'is_null', 'is_not_null'] } }
			}
		}
	};

	return merge(BasicConfig, config);
};

export default QueryBuilderConfig;
